/**
 * The reset-panels broadcast, and the registry of what it clears.
 *
 * With several draggable, hidable floaters on screen, a user WILL eventually lose one — dragged
 * somewhere unreachable, hidden and the edge tab not spotted, or a stale position left behind by
 * an older layout. One button has to bring every one of them home.
 *
 * Two halves, and both are required:
 *
 *  1. **The broadcast.** `window.dispatchEvent(new CustomEvent(RESET_FLOATING_PANELS))`. A window
 *     event, not a `storage` write — the `storage` event only fires in OTHER tabs, so writing
 *     localStorage alone cannot reset the components in the tab the user is looking at.
 *
 *  2. **Self-clearing.** `useDraggableFloat` persists a position only on drag-END, so a
 *     programmatic `setPosition` in a reset handler never touches storage. A dispatch-only reset
 *     therefore snaps floaters home on screen and lets their old positions return on reload.
 *     Every listener must ALSO remove its own keys — which is what `clearFloatingKeys` is for.
 *
 * The event name is a LITERAL STRING on both sides, deliberately. App-shell components can't
 * import a module's file and a module can't import another module's, so the contract is the
 * string. Rename it and you rename it everywhere by hand: grep `tms:reset-floating-panels`.
 *
 * Full pattern: vault/how-to/draggable-floating-launchers.md.
 */

/** The one event name. Duplicated as a literal wherever an import would cross a module boundary. */
export const RESET_FLOATING_PANELS = 'tms:reset-floating-panels';

/**
 * Every localStorage key a floater owns, by prefix. Positions, hidden flags and open flags all
 * live under these, so the reset can clear keys belonging to floaters that aren't even mounted
 * on the current page.
 */
export const FLOATING_KEY_PREFIXES = [
  'tlp_module_nav_',           // the module navigation launcher (position + hidden), per module per user
  'tlp_floating_navbar_',      // the app-wide navigation launcher (position + hidden)
  'floatingchat.',           // the floating chat bubble + panel (fab.pos / panel.pos / open / hidden)
  'calendars.',              // calendar settings FAB + panel, calendar menu + nav launchers
  'emails.',                 // the emails nav launcher
  'p2_cart_',                // the P2 floating cart (position + hidden), per user
];

/** Remove every stored position / hidden / open flag belonging to a floating launcher. */
export function clearFloatingKeys(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && FLOATING_KEY_PREFIXES.some((p) => key.startsWith(p))) doomed.push(key);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* private browsing — the broadcast below still snaps everything home for this session */
  }
}

/**
 * Clear the stored state, then tell every mounted floater to snap home. Called by the footer's
 * "↻ Reset floating panels" — which lives in the footer, not inside any launcher's own menu,
 * because a reset reachable only by opening the launcher is useless exactly when the launcher
 * is the thing that's lost.
 */
export function resetFloatingPanels(): void {
  clearFloatingKeys();
  window.dispatchEvent(new CustomEvent(RESET_FLOATING_PANELS));
}
